import React from 'react';

import cn from 'classnames';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { downloadFile } from '@shared/helpers';
import { PrevFileFrame } from '@shared/ui';
import { useLightbox } from '@shared/ui/Lightbox/lib/hooks/useLightbox';
import { NextIcon, DownloadIcon, PrevIcon } from '@shared/ui/Lightbox/ui/Icons';

import cls from './Lightbox.module.css';

export interface LightboxFile {
    id: number;
    fileSrc: string;
    description?: string;
    author?: {
        name: string;
        avatarUrl: string;
    };
    fileName?: string;
    mimeType?: string;
}

interface LightboxProps {
    classes?: string;
    headerSlot?: React.ReactNode;
    files: LightboxFile[];
    onClose: () => void;
    currentFileId: number;
}

export const Lightbox = ({ classes, headerSlot, files, onClose, currentFileId }: LightboxProps) => {
    const {
        showPrev,
        showNext,
        decodeURL,
        iframeSrc,
        isFileImg,
        currentIndex,
        currentFile,
        isPrevFormat,
    } = useLightbox(currentFileId, files, onClose);
    const { t } = useTranslation();
    if (!files.length) return null;

    if (currentIndex === -1) return null;

    return createPortal(
        <div className={cn(cls.Lightbox, classes)} onClick={onClose}>
            <div className={cls.LightboxOverlay}>
                <div className={cls.LightboxContent} onClick={(e) => e.stopPropagation()}>
                    {headerSlot}
                    <div key={currentFile.id}>
                        {isPrevFormat ? (
                            <>
                                {isFileImg ? (
                                    <div className={cls.imageWrapper}>
                                        <img
                                            src={currentFile.fileSrc}
                                            alt={currentFile.fileName || 'photo'}
                                        />
                                        {currentFile.description && (
                                            <div className={cn(cls.ImageDescription, [classes])}>
                                                <div className={cls.descriptionBody}>
                                                    {currentFile.description && (
                                                        <div className={cls.description}>
                                                            {currentFile.description}
                                                        </div>
                                                    )}
                                                    {currentFile.author && (
                                                        <div className={cls.author}>
                                                            <img
                                                                src={currentFile.author.avatarUrl}
                                                                alt={currentFile.author.name}
                                                                className={cls.avatar}
                                                            />
                                                            <div className={cls.authorName}>
                                                                {currentFile.author.name}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <PrevFileFrame decodeURL={decodeURL} iframeSrc={iframeSrc} />
                                )}
                            </>
                        ) : (
                            <div
                                className={cn(cls.notPrevFormatWrapper)}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <div className={cls.notPrevFormatBody}>
                                    {t('PrevModal.return.customModalContent.notPrevFormatBody')}
                                </div>
                                <div className={cls.notPrevFormatFooter}>
                                    <button
                                        onClick={async () => {
                                            await downloadFile(
                                                currentFile.fileSrc,
                                                currentFile.fileName
                                            );
                                        }}
                                    >
                                        <DownloadIcon />
                                        {t('PrevModal.return.notPrevFormatFooter.saveButton.text')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={cls.prevFileArrow}>
                        {currentIndex !== 0 && (
                            <button className={cls.navigationButton} onClick={showPrev}>
                                <PrevIcon />
                            </button>
                        )}
                    </div>
                    <div className={cls.nextFileArrow}>
                        {currentIndex !== files.length - 1 && (
                            <button className={cls.navigationButton} onClick={showNext}>
                                <NextIcon />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};
